import React, { FC, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { Pagination, ReactTable, ReactTableUtils } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { SortingState } from '@tanstack/react-table';

import { StatsState } from '~/core/types';
import { HEADER_HEIGHT, isWebview } from '~/core/utils';
import { dataTableActionCreators } from '~/redux/ducks/playersAndTeams/dataTable';
import Analytics from '~/utility/analytics';
import { commonMessages } from '~/utility/commonMessages';

// TODO look into deleting this
import './_print.pageWrapper.scss';
import { generateColumnsForMeasures } from './utils/generateColumns';
import { handleStickyScroll, syncScroll } from './utils/sticky';

import { LoadingContainer, ReactTableContainer, StickyHeaderFiller } from './styles';

const RESIZE_EVENT = 'resize';
const SCROLL_EVENT = 'scroll';

// This is the state we want when there is a 500 message or another invalid state
const DEFAULT_DATA_STATE = { data: [], total: 0 };

const StatsTable: FC = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const theme = useTheme();

    const stickyTableRef = useRef<HTMLElement>(null);

    const statsConfig = useSelector((state: StatsState) => state.config);
    const appliedReportState = useSelector((state: StatsState) => state.playersAndTeams.report.applied);
    const dataTableState = useSelector((state: StatsState) => state.playersAndTeams.dataTable);
    const appliedFilters = useSelector((state: StatsState) => state.playersAndTeams.filter.applied);
    const reportCategory = useSelector((state: StatsState) => state.playersAndTeams.report.reportCategory);

    const { dataset: dataRows, isFetching, sort } = dataTableState;
    const dataset = dataRows?.data ? dataRows : DEFAULT_DATA_STATE;
    const totalRows = dataset.total;

    useEffect(() => {
        if (!isWebview()) {
            return;
        }

        window.googletag = window.googletag || { cmd: [] };

        const scrollToTable = () => {
            stickyTableRef.current.scrollIntoView();
        };

        window.googletag.cmd.push(() => {
            // If no ads are present scroll now, otherwise scroll after they have loaded
            if (window.googletag.pubads().getSlots().length === 0) {
                scrollToTable();
            } else {
                window.googletag.pubads().addEventListener('slotRenderEnded', scrollToTable);
            }
        });

        return () => {
            window.googletag = window.googletag || { cmd: [] };

            window.googletag.cmd.push(() => {
                window.googletag.pubads().removeEventListener('slotRenderEnded', scrollToTable);
            });
        };
    }, []);

    // Functions
    const statsTableUpdated = (action: string) => {
        Analytics.sendGaReportEvent(action, appliedReportState, appliedFilters, statsConfig.franchiseData);
    };

    const onPageChange = (pageNumber: number) => {
        statsTableUpdated('next');
        dispatch(dataTableActionCreators.setPage(pageNumber));
    };

    const onSortingChange = (sortingState: SortingState) => {
        statsTableUpdated('sort');
        dispatch(dataTableActionCreators.setSort(sortingState));
    };

    // Effects
    // This effect makes sure we're on a valid page. Let's say there are 100 total rows, and we're on the 10th page
    // seeing rows 91 - 100. If the page size is changed to 50 we update the page index to 2 so we see rows 51 - 100
    useEffect(() => {
        const correctedPageSize = Math.min(dataTableState.pageSize, totalRows);
        const maxPages = Math.ceil(totalRows / correctedPageSize);

        if (dataTableState.page > maxPages) {
            onPageChange(maxPages - 1);
        }
    }, [dataTableState.page, dataTableState.pageSize, totalRows]);

    // Columns can be derived from the appliedReportState and is not directly accessible from the user's POV
    // i.e. They need to change the report to change the cols unlike the sort or page size/number
    const datasetColumns = dataset.data[0] ? Object.keys(dataset.data[0]) : [];
    const correctedPageSize = Math.min(dataTableState.pageSize, totalRows);
    const pages = correctedPageSize === 0 ? 0 : Math.ceil(totalRows / correctedPageSize);
    const startingRowIndex = dataTableState.page * correctedPageSize;
    const columns = generateColumnsForMeasures(
        statsConfig,
        appliedReportState,
        reportCategory,
        intl,
        datasetColumns,
        theme,
        startingRowIndex,
    );

    const pinnedSize = useMemo(() => ReactTableUtils.getLeftPinnedSize(columns), [columns]);

    useEffect(() => {
        if (isFetching || dataset.data.length === 0) {
            return;
        }

        document.body.addEventListener(SCROLL_EVENT, handleStickyScroll(HEADER_HEIGHT, stickyTableRef.current));
        window.addEventListener(RESIZE_EVENT, handleStickyScroll(HEADER_HEIGHT, stickyTableRef.current));

        // This keeps the Table Body and Header in sync when either scrolls
        const tableContainer = stickyTableRef.current?.getElementsByClassName('rt-table-container')[0];
        const tableHeader = stickyTableRef.current?.getElementsByClassName('rt-thead')[0];

        tableContainer?.addEventListener(SCROLL_EVENT, syncScroll(tableHeader));
        tableHeader?.addEventListener(SCROLL_EVENT, syncScroll(tableContainer));

        return () => {
            document.body.removeEventListener(SCROLL_EVENT, handleStickyScroll(HEADER_HEIGHT, stickyTableRef.current));
            window.removeEventListener(RESIZE_EVENT, handleStickyScroll(HEADER_HEIGHT, stickyTableRef.current));

            tableContainer?.removeEventListener(SCROLL_EVENT, syncScroll(tableHeader));
            tableHeader?.removeEventListener(SCROLL_EVENT, syncScroll(tableContainer));
        };
    }, [isFetching, dataset.data.length]);

    return (
        <span ref={stickyTableRef}>
            {
                isFetching && (
                    <LoadingContainer>
                        <LoadingDisplay isLoading isInline size={60} />
                    </LoadingContainer>
                )
            }
            {
                !isFetching && (dataset.data?.length === 0
                    ? intl.formatMessage(commonMessages.labels.noDataToDisplay)
                    : (
                        <>
                            <ReactTableContainer $pinnedSize={pinnedSize}>
                                <StickyHeaderFiller className="sticky-header-filler" />
                                <ReactTable
                                    data={isFetching ? [] : dataset.data}
                                    columns={columns}
                                    initialSorting={sort}
                                    manualSorting
                                    onSortingChange={onSortingChange}
                                />
                            </ReactTableContainer>
                            <Pagination
                                handlePageUpdated={(pageIndex: number) => {
                                    dispatch(dataTableActionCreators.setPage(pageIndex));
                                }}
                                initialPageIndex={dataTableState.page}
                                pageCount={pages}
                            />
                        </>
                    )
                )
            }
        </span>
    );
};

export default React.memo(StatsTable);
